import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Breadcrumbs,
  IconButton,
  InputAdornment,
  Link,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import { apiFetch } from '../api/client';

const buildQuery = (params = {}) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([key, val]) => {
    if (val !== undefined && val !== null && val !== '') {
      qs.append(key, val);
    }
  });
  return qs.toString();
};

const toLocalIsoDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const AuditTrailPage = () => {
  const todayIso = useMemo(() => toLocalIsoDate(new Date()), []);
  const defaultStart = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return toLocalIsoDate(d);
  }, []);

  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ totalItems: 0 });
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortBy, setSortBy] = useState('createdDatetime');
  const [sortDir, setSortDir] = useState('desc');
  const [filters, setFilters] = useState({
    search: '',
    startDate: defaultStart,
    endDate: todayIso
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [menuAnchor, setMenuAnchor] = useState(null);

  const columns = [
    { id: 'createdDatetime', label: 'Datetime' },
    { id: 'user', label: 'User' },
    { id: 'activity', label: 'Activity' }
  ];

  const handleFilterChange = (key) => (event) => {
    setFilters((prev) => ({ ...prev, [key]: event.target.value }));
    setPage(0);
  };

  const handleSort = (columnId) => {
    const isAsc = sortBy === columnId && sortDir === 'asc';
    setSortBy(columnId);
    setSortDir(isAsc ? 'desc' : 'asc');
  };

  const fetchLogs = async () => {
    setLoading(true);
    setError('');
    try {
      const query = buildQuery({
        page: page + 1,
        pageSize,
        search: filters.search || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        sortBy,
        sortDir
      });
      const data = await apiFetch(`/api/audit-trail${query ? `?${query}` : ''}`);
      const list = Array.isArray(data.data) ? data.data : data.data?.items || [];
      setRows(list);
      setMeta(data.meta || { totalItems: list.length });
    } catch (err) {
      setError(err.message || 'Failed to load audit trail');
      setRows([]);
      setMeta({ totalItems: 0 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [page, pageSize, filters, sortBy, sortDir]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDownload = async () => {
    try {
      const query = buildQuery({
        search: filters.search || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        sortBy,
        sortDir
      });
      const resp = await apiFetch(`/api/audit-trail/export${query ? `?${query}` : ''}`, {
        parseAs: 'blob'
      });
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'audit-trail.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || 'Failed to download Excel');
    }
  };

  const handleOpenMenu = (event) => setMenuAnchor(event.currentTarget);
  const handleCloseMenu = () => setMenuAnchor(null);

  const totalCount = meta?.totalItems ?? rows.length;

  return (
    <Stack spacing={3}>
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2
        }}
      >
        <Box>
          <Breadcrumbs
            aria-label="breadcrumb"
            separator="/"
            sx={{ fontSize: 12, mb: 1, color: 'text.secondary' }}
          >
            <Link underline="hover" color="inherit" href="#">
              Dashboard
            </Link>
            <Link underline="hover" color="inherit" href="#">
              Management
            </Link>
            <Typography color="text.primary">Audit Trail</Typography>
          </Breadcrumbs>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Audit Trail
          </Typography>
          <Typography color="text.secondary">
            Review user activities across the platform.
          </Typography>
        </Box>
        <IconButton
          aria-label="audit trail actions"
          onClick={handleOpenMenu}
          sx={{ alignSelf: { xs: 'flex-start', md: 'center' } }}
        >
          <MoreVertIcon />
        </IconButton>
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleCloseMenu}
          PaperProps={{
            sx: {
              background: 'linear-gradient(90deg, #4f46e5 0%, #06b6d4 100%)',
              color: '#fff',
              '& .MuiMenuItem-root': {
                color: 'inherit',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.08)'
                }
              }
            }
          }}
        >
          <MenuItem
            onClick={() => {
              handleCloseMenu();
              handleDownload();
            }}
          >
            <DownloadIcon fontSize="small" style={{ marginRight: 8 }} />
            Download Excel
          </MenuItem>
        </Menu>
      </Box>

      <Paper
        sx={{
          p: { xs: 2, sm: 3 },
          boxShadow: '0px 24px 48px rgba(15, 23, 42, 0.05)'
        }}
      >
        <Stack spacing={2.5}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
            <TextField
              label="Start Date"
              type="date"
              value={filters.startDate}
              onChange={handleFilterChange('startDate')}
              InputLabelProps={{ shrink: true }}
              sx={{ width: { xs: '100%', md: 220 } }}
            />
            <TextField
              label="End Date"
              type="date"
              value={filters.endDate}
              onChange={handleFilterChange('endDate')}
              InputLabelProps={{ shrink: true }}
              sx={{ width: { xs: '100%', md: 220 } }}
            />
            <TextField
              value={filters.search}
              onChange={handleFilterChange('search')}
              placeholder="Search name / email / activity"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                )
              }}
              sx={{
                flexGrow: 1,
                width: '100%',
                boxSizing: 'border-box',
                minWidth: 0
              }}
            />
          </Stack>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        <Box
          sx={{
            width: '100%',
            overflowX: 'auto',
            border: '1px solid rgba(148, 163, 184, 0.16)',
            borderRadius: 1,
            mt: 3,
            backgroundColor: 'background.paper',
            '&::-webkit-scrollbar': {
              height: 6
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(148, 163, 184, 0.35)',
              borderRadius: 999
            }
          }}
        >
          <TableContainer
            sx={{
              display: 'inline-block',
              minWidth: '100%',
              width: '100%'
            }}
          >
            <Table sx={{ minWidth: 800, borderCollapse: 'separate', borderSpacing: 0 }}>
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      sortDirection={sortBy === column.id ? sortDir : false}
                    >
                      {column.sortable === false ? (
                        column.label
                      ) : (
                        <TableSortLabel
                          active={sortBy === column.id}
                          direction={sortBy === column.id ? sortDir : 'asc'}
                          onClick={() => handleSort(column.id)}
                        >
                          {column.label}
                        </TableSortLabel>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow
                    hover
                    key={row.id || `${row.userEmail || row.email}-${row.createdDatetime || row.created_datetime}`}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'rgba(15, 23, 42, 0.02)'
                      }
                    }}
                  >
                    <TableCell>{row.createdDatetime || row.created_datetime || '-'}</TableCell>
                    <TableCell>
                      <Typography fontWeight={700}>{row.userName || row.name || '-'}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {row.userEmail || row.email || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 360, whiteSpace: 'normal' }}>
                      {row.activity || '-'}
                    </TableCell>
                  </TableRow>
                ))}
                {!rows.length && !loading && (
                  <TableRow>
                    <TableCell colSpan={columns.length}>
                      <Box sx={{ textAlign: 'center', py: 5 }}>
                        <Typography variant="subtitle1" fontWeight={600}>
                          No records found
                        </Typography>
                        <Typography color="text.secondary">
                          Adjust filters to see available records.
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <TablePagination
          component="div"
          count={totalCount}
          rowsPerPage={pageSize}
          rowsPerPageOptions={[5, 10, 25, 50]}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            const next = parseInt(event.target.value, 10);
            setPageSize(next);
            setPage(0);
          }}
          sx={{
            '& .MuiTablePagination-toolbar': {
              px: { xs: 0, sm: 2 }
            }
          }}
        />
      </Paper>
    </Stack>
  );
};

export default AuditTrailPage;
